// src/pages/Catalog.jsx
import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { PublicAPI } from '../api/public';
import { useAppStore } from '../store/useAppStore';
import { useCurrencyStore } from '../store/useCurrencyStore';
import FilterBar from '../components/FilterBar';
import ListingCard from '../components/ListingCard';
import YMap from '../components/YMap';

const Title = styled.h1`margin: 14px 0;`;
const Grid = styled.div`
  display: grid; gap: 14px;
  grid-template-columns: 1fr 1fr;
  @media (max-width: 860px){ grid-template-columns: 1fr; }
`;
const Pager = styled.div`
  display:flex; gap:10px; justify-content:center; margin:14px 0 28px;
`;

export default function Catalog({ type = 'primary' }) {
  const { t, i18n } = useTranslation();
  const regionId = useAppStore(s => s.regionId);
  const currency = useCurrencyStore(s => s.currency);
  const [dicts, setDicts] = React.useState({ propertyTypes: [], dealTypes: [] });

  React.useEffect(() => {
    (async () => {
      try {
        const cfg = await PublicAPI.config();
        setDicts({
          propertyTypes: cfg?.property_types || [],
          dealTypes:     cfg?.deal_types     || [],
        });
      } catch {}
    })();
  }, []);

  const [items, setItems] = React.useState([]);
  const [page, setPage] = React.useState(1);
  const [total, setTotal] = React.useState(0);
  const [loading, setLoading] = React.useState(false);
  const [err, setErr] = React.useState('');
  const [params, setParams] = React.useState({}); // текущие фильтры

  const titleByType = {
    primary: t('newbuild') || 'Новостройки',
    commercial: t('commercial') || 'Коммерческая',
    secondary: t('secondary') || 'Вторичная',
    rent: t('rent') || 'Аренда',
    foreign: t('nav.foreign') || 'Зарубежная недвижимость',
    suburban: t('nav.suburban') || 'Загородная недвижимость',
    commercialRent: t('nav.commercialRent') || 'Коммерческая аренда',
    mansions: t('nav.mansions') || 'Особняки',
    parking: t('nav.parking') || 'Паркинги',
  };

  // Базовые фильтры по типу страницы (slug-ами, как ждёт бэк)
  React.useEffect(() => {
    const base = {};
    if (type === 'commercial') base.property_type = 'commercial';
    if (type === 'rent')       base.deal_type     = 'rent';
    if (type === 'secondary')  base.deal_type     = 'secondary';
    if (type === 'primary')    base.deal_type     = 'new-build'; // ВАЖНО: slug с бэка
    if (type === 'foreign')    base.property_type = 'foreign';
    if (type === 'suburban')   base.property_type = 'suburban';
    if (type === 'commercialRent') {
      base.property_type = 'commercial';
      base.deal_type = 'rent';
    }
    if (type === 'mansions')   base.property_type = 'mansion';
    if (type === 'parking')    base.property_type = 'parking';
    setItems([]);
    setPage(1);
    setParams(base);
  }, [type]);

  // Подхватываем фильтры из query-string (например, ?bedrooms=2&unit_type=duplex)
  React.useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    const p = {};
    // поддерживаем известные параметры
    if (sp.get('property_type')) p.property_type = sp.get('property_type');
    if (sp.get('deal_type')) p.deal_type = sp.get('deal_type');
    if (sp.get('district_id')) p.district_id = Number(sp.get('district_id'));
    if (sp.get('bedrooms')) {
      const n = Number(sp.get('bedrooms'));
      if (!Number.isNaN(n)) {
        p.bedrooms = n; p.bedrooms_min = n; p.bedrooms_max = n;
      }
    }
    if (sp.get('price_min')) p.price_min = Number(sp.get('price_min'));
    if (sp.get('price_max')) p.price_max = Number(sp.get('price_max'));
    if (sp.get('area_min')) p.area_min = Number(sp.get('area_min'));
    if (sp.get('area_max')) p.area_max = Number(sp.get('area_max'));
    // алиасы из примера (?unit_type=duplex)
    if (sp.get('unit_type') && !p.property_type) p.property_type = sp.get('unit_type');
    if (Object.keys(p).length) {
      setItems([]); setPage(1); setParams(prev => ({ ...prev, ...p }));
    }
  }, []);

  React.useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setErr('');
      try {
        const data = await PublicAPI.properties({
          ...(params || {}),
          ...(type!=='map'?{page,page_size:8}:{}),
          region_id: regionId || undefined,
          currency: currency || undefined,
        });
        const list = Array.isArray(data?.properties) ? data.properties : [];
        const tot = data?.pagination?.total ?? 0;
        setItems(list);
        setTotal(tot);
      } catch (e) {
        if (!cancelled) {
          setErr(e.message || 'Error');
          setItems([]);
          setTotal(0);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [params, page, regionId, currency]);

  const handleSearch = (p) => {
    const out = { ...(p || {}) };
    // если пришёл property_type_id и нет slug — подставим slug
    if (out.property_type_id && !out.property_type) {
      const pt = dicts.propertyTypes.find(x => String(x.id) === String(out.property_type_id));
      if (pt?.slug) {
        out.property_type = pt.slug;
        delete out.property_type_id;
      }
    }
    // если пришёл deal_type_id и нет slug — подставим slug
    if (out.deal_type_id && !out.deal_type) {
      const dt = dicts.dealTypes.find(x => String(x.id) === String(out.deal_type_id));
      if (dt?.slug) {
        out.deal_type = dt.slug;
        delete out.deal_type_id;
      }
    }
    setItems([]);
    setPage(1);
    setParams(out);
    // синхронизация с URL
    const usp = new URLSearchParams();
    Object.entries(out).forEach(([k,v]) => { if (v !== '' && v != null) usp.set(k, String(v)); });
    const url = `${window.location.pathname}?${usp.toString()}`;
    window.history.replaceState(null, '', url);
  };

  const totalPages = Math.max(1, Math.ceil((total || 0) / 8));
  const goTo = (n) => {
    setPage(Math.max(1, Math.min(totalPages, n)));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Мемоизируем markers, чтобы не пересоздавать массив при каждом рендере
  const markers = React.useMemo(() => {
    return items
      .map(it => {
        const lat = Number(it.lat);
        const lng = Number(it.lng);
        return {
          coords: [lat, lng],
          title: it.title,
          slug: it.slug,
          id: it.id,
          price: it.price,
        };
      })
      .filter(m => Number.isFinite(m.coords[0]) && Number.isFinite(m.coords[1]));
  }, [items]);

  const DEFAULT_CENTER = [55.751244, 37.618423];

  const mapCenter = React.useMemo(() => {
    const lat = Number(items?.[0]?.lat);
    const lng = Number(items?.[0]?.lng);
    if (Number.isFinite(lat) && Number.isFinite(lng)) return [lat, lng];
    return DEFAULT_CENTER;
  }, [items]);

  return (
    <main className="container" style={{ padding: '10px 0 20px' }}>
      <Title>{titleByType[type] || (t('catalog.title') || 'Каталог')}</Title>

      <FilterBar
        mode={type === 'primary' ? 'newbuild' : type}
        onSearch={handleSearch}
        initialParams={params}
        isMap={type === 'map'}
      />

      {err && <div style={{ color: '#c62828', marginBottom: 10 }}>{err}</div>}
      {!loading && !items.length && <div style={{ opacity: .75 }}>{t('common.nothingFound') || 'Ничего не найдено'}</div>}
      
      {
        type !== 'map' ?(
          <Grid>
            {items.map(it => (
              <ListingCard key={it.id ?? it.slug ?? Math.random()} item={it} />
            ))}
          </Grid>
        ):
          <YMap
            apiKey={process.env.REACT_APP_YMAPS_KEY}
            lang={i18n.language === 'en' ? 'en_US' : 'ru_RU'}
            center={mapCenter}
            zoom={14}
            markers={markers}
            isLink={true}
            clusterize={true}
          />
        
      }

      {type !== 'map' && totalPages > 1 && (
        <Pager>
          <button className="btn" onClick={()=>goTo(page-1)} disabled={page<=1}>‹ Назад</button>
          {Array.from({length: totalPages}, (_,i)=>i+1).map(n=>(
            <button key={n} className="btn" data-active={n===page} onClick={()=>goTo(n)}>{n}</button>
          ))}
          <button className="btn" onClick={()=>goTo(page+1)} disabled={page>=totalPages}>Вперёд ›</button>
        </Pager>
      )}
    </main>
  );
}
